import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { LayoutGrid, CalendarDays, GanttChartSquare, Search } from "lucide-react";
import { useApi, fetchTasks } from "../api";
import { StatusPill, PriorityPill, Avatar, PageHeader, Loading, ErrorState, statusColor, formatDate } from "../components/common";
import { Input } from "../components/ui/input";
import TaskDetailSheet from "../components/TaskDetailSheet";
import { useAuth } from "../context/AuthContext";

function TaskCard({ t, onOpen }) {
  return (
    <div onClick={() => onOpen(t.id)} className="cursor-pointer rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-start justify-between gap-2"><p className="text-sm font-semibold text-slate-800">{t.title}</p><PriorityPill value={t.priority} /></div>
      <p className="mt-1 text-xs text-slate-400">{t.project}</p>
      {t.phase && <div className="mt-2"><span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">{t.phase}</span></div>}
      <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2">
        <span className="text-xs text-slate-400">{formatDate(t.dueDate)}</span>
        <Avatar avatar={t.assigneeAvatar} size={26} />
      </div>
    </div>
  );
}

function KanbanView({ list, statuses, onOpen }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {statuses.map((col) => {
        const items = list.filter((t) => t.status === col);
        return (
          <div key={col} className="rounded-2xl bg-slate-100/70 p-3">
            <div className="mb-3 flex items-center gap-2 px-1">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: statusColor(col).dot }} />
              <h3 className="text-sm font-bold text-slate-700">{col}</h3>
              <span className="ml-auto rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-500">{items.length}</span>
            </div>
            <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">{items.map((t) => <TaskCard key={t.id} t={t} onOpen={onOpen} />)}</div>
          </div>
        );
      })}
    </div>
  );
}

function CalendarView({ list, initialDate, onOpen }) {
  const [ref, setRef] = useState(initialDate);
  const year = ref.getFullYear(), month = ref.getMonth();
  const first = new Date(year, month, 1);
  const startDay = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const monthLabel = ref.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const tasksOn = (day) => list.filter((t) => {
    if (!t.dueDate) return false;
    const dt = new Date(t.dueDate);
    return dt.getFullYear() === year && dt.getMonth() === month && dt.getDate() === day;
  });
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-bold text-slate-900">{monthLabel}</h3>
        <div className="flex gap-1">
          <button onClick={() => setRef(new Date(year, month - 1, 1))} className="rounded-lg border border-slate-200 px-3 py-1 text-sm font-semibold text-slate-600 hover:bg-slate-50">Prev</button>
          <button onClick={() => setRef(new Date(year, month + 1, 1))} className="rounded-lg border border-slate-200 px-3 py-1 text-sm font-semibold text-slate-600 hover:bg-slate-50">Next</button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase text-slate-400">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => <div key={d} className="py-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => (
          <div key={i} className={`min-h-[92px] rounded-lg border p-1.5 ${d ? "border-slate-100 bg-white" : "border-transparent bg-slate-50/50"}`}>
            {d && <><p className="mb-1 text-xs font-semibold text-slate-500">{d}</p>
              <div className="space-y-1">{tasksOn(d).slice(0, 4).map((t) => (
                <div key={t.id} onClick={() => onOpen(t.id)} className="cursor-pointer truncate rounded px-1.5 py-0.5 text-[10px] font-semibold hover:opacity-80" style={{ backgroundColor: statusColor(t.status).bg, color: statusColor(t.status).text }} title={t.title}>{t.title}</div>
              ))}</div></>}
          </div>
        ))}
      </div>
    </div>
  );
}

function TimelineView({ list, onOpen }) {
  const withDates = list.filter((t) => t.startDate && t.dueDate).map((t) => ({ ...t, s: new Date(t.startDate), e: new Date(t.dueDate) }));
  if (withDates.length === 0) return <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">No tasks with start &amp; due dates to plot.</div>;

  // Pad range to whole months for cleaner gridlines
  const rawMin = new Date(Math.min(...withDates.map((t) => t.s)));
  const rawMax = new Date(Math.max(...withDates.map((t) => t.e)));
  const min = new Date(rawMin.getFullYear(), rawMin.getMonth(), 1);
  const max = new Date(rawMax.getFullYear(), rawMax.getMonth() + 1, 0);
  const span = (max - min) || 1;
  const sorted = [...withDates].sort((a, b) => a.s - b.s);

  // Month gridlines / labels
  const months = [];
  let cur = new Date(min.getFullYear(), min.getMonth(), 1);
  while (cur <= max) {
    months.push(new Date(cur));
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }
  const pos = (d) => ((d - min) / span) * 100;
  const statuses = [...new Set(sorted.map((t) => t.status))];

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 border-b border-slate-100 px-5 py-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Timeline</span>
        <div className="flex flex-wrap items-center gap-3">
          {statuses.map((s) => (
            <span key={s} className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: statusColor(s).dot }} /> {s}
            </span>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[820px]">
          {/* Month header */}
          <div className="flex border-b border-slate-100 bg-slate-50/60">
            <div className="w-64 shrink-0 border-r border-slate-100 px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Task</div>
            <div className="relative flex-1">
              <div className="flex h-full">
                {months.map((m, i) => (
                  <div key={i} className="flex-1 border-r border-slate-100 px-2 py-2.5 text-xs font-semibold text-slate-500">
                    {m.toLocaleDateString("en-GB", { month: "short", year: "2-digit" })}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Rows */}
          <div>
            {sorted.map((t, idx) => {
              const left = pos(t.s);
              const width = Math.max(pos(t.e) - pos(t.s), 1.5);
              const c = statusColor(t.status);
              const days = Math.max(1, Math.round((t.e - t.s) / 86400000));
              return (
                <div
                  key={t.id}
                  onClick={() => onOpen(t.id)}
                  className={`flex cursor-pointer items-stretch transition-colors hover:bg-slate-50 ${idx % 2 ? "bg-white" : "bg-slate-50/30"}`}
                >
                  {/* Full task name (no truncation) */}
                  <div className="flex w-64 shrink-0 flex-col justify-center gap-0.5 border-r border-slate-100 px-5 py-3">
                    <p className="text-sm font-semibold leading-snug text-slate-800" style={{ overflowWrap: "anywhere" }}>{t.title}</p>
                    <p className="text-[11px] text-slate-400" style={{ overflowWrap: "anywhere" }}>{t.project}</p>
                  </div>

                  {/* Bar lane with month gridlines */}
                  <div className="relative flex-1 py-3">
                    <div className="pointer-events-none absolute inset-0 flex">
                      {months.map((m, i) => <div key={i} className="flex-1 border-r border-slate-100/70" />)}
                    </div>
                    <div className="relative flex h-8 items-center">
                      <div
                        className="absolute flex h-7 items-center rounded-md px-2 shadow-sm ring-1 ring-black/5"
                        style={{ left: `${left}%`, width: `${width}%`, backgroundColor: c.bg }}
                      >
                        <span className="h-full w-1 shrink-0 rounded-full" style={{ backgroundColor: c.dot }} />
                        <span className="ml-2 whitespace-nowrap text-[11px] font-semibold" style={{ color: c.text }}>{days}d</span>
                      </div>
                      <div className="absolute text-[11px] font-medium text-slate-400" style={{ left: `calc(${Math.min(left + width, 100)}% + 8px)` }}>
                        {formatDate(t.e)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Tasks() {
  const [view, setView] = useState("kanban");
  const [q, setQ] = useState("");
  const [mine, setMine] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: tasks, loading, error, reload } = useApi(fetchTasks);

  const openTask = (id) => { setSelectedId(id); setSheetOpen(true); };

  // Deep-link from a notification: /tasks?open=<id>
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) {
      openTask(openId);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const initialDate = useMemo(() => {
    if (!tasks || !tasks.length) return new Date();
    const dated = tasks.filter((t) => t.dueDate).map((t) => new Date(t.dueDate)).sort((a, b) => a - b);
    return dated.length ? new Date(dated[0].getFullYear(), dated[0].getMonth(), 1) : new Date();
  }, [tasks]);

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (loading || !tasks) return <Loading />;

  const statuses = [...new Set(tasks.map((t) => t.status))];
  const list = tasks.filter((t) => {
    const matchQ = (t.title + t.project + (t.phase || "")).toLowerCase().includes(q.toLowerCase());
    const matchMine = !mine || t.assigneeId === user?.id || t.assignee === user?.name;
    return matchQ && matchMine;
  });
  const mineCount = tasks.filter((t) => t.assigneeId === user?.id || t.assignee === user?.name).length;
  const tabs = [{ id: "kanban", label: "Kanban", icon: LayoutGrid }, { id: "calendar", label: "Calendar", icon: CalendarDays }, { id: "timeline", label: "Timeline", icon: GanttChartSquare }];

  return (
    <div className="mx-auto max-w-7xl animate-fade-in-up">
      <PageHeader title="Tasks" subtitle={`${tasks.length} live tasks \u00b7 click any task to view, edit & comment`} />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tasks..." className="h-9 border-slate-200 bg-white pl-9 text-sm" />
        </div>
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
          <button onClick={() => setMine(false)} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${!mine ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>All tasks</button>
          <button onClick={() => setMine(true)} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${mine ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>My tasks ({mineCount})</button>
        </div>
        <div className="ml-auto flex items-center gap-1 rounded-lg bg-slate-100 p-1">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setView(t.id)} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold ${view === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}><t.icon className="h-4 w-4" /> {t.label}</button>
          ))}
        </div>
      </div>
      {mine && mineCount === 0 && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">No tasks are currently assigned to you in QuickBase. Showing your assigned work here as soon as tasks are allocated.</div>
      )}
      {view === "kanban" && <KanbanView list={list} statuses={statuses} onOpen={openTask} />}
      {view === "calendar" && <CalendarView list={list} initialDate={initialDate} onOpen={openTask} />}
      {view === "timeline" && <TimelineView list={list} onOpen={openTask} />}

      <TaskDetailSheet taskId={selectedId} open={sheetOpen} onOpenChange={setSheetOpen} onSaved={reload} />
    </div>
  );
}
